import { useEffect, useMemo, useState, useRef, type ReactNode } from "react";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  CheckboxGrid,
  ChipTextField,
  ColorPicker,
  Field,
  RepeaterList,
  Section,
  Select,
  TagPicker,
  Textarea,
  TextInput,
} from "../components/Form";
import {
  autofillTargetAudience,
  autofillLocalDetails,
  formatErrorForAlert,
  masterAutofill,
  parseGBP,
  type ParsedGBP,
  updateClient,
} from "../lib/api";
import { useToast } from "../components/Toast";
import { registerUnsavedGuard, clearUnsavedGuard } from "../lib/unsavedGuard";
import type { WorkspaceContext } from "./ClientWorkspacePage";
import { SettingsSidebar, type SettingsNavSection } from "./ClientSettingsSidebar";
import { GbpServicesEditor } from "./GbpServicesEditor";
import { PromptOverrides, HowCustomPromptsWork } from "./PromptOverrides";
import {
  calculateQualityScore,
  findMissingRequiredFields,
  getScoreBand,
  getScoreGaps,
  isFieldFilled,
} from "./qualityScore";
import {
  type CTAButton,
  type FormState,
  type TrustBadge,
  DEFAULT_EXTERNAL_DOMAINS,
  IMAGE_STYLE_OPTIONS,
  clientToFormState,
  formStateToUpdatePayload,
  parseGbpServicesText,
} from "./settingsForm";

const TONE_OPTIONS = [
  "Professional",
  "Friendly & Warm",
  "Authoritative & Expert",
  "Casual & Conversational",
  "Luxurious & Premium",
  "Compassionate & Caring",
  "Technical & Precise",
  "Energetic & Bold",
];

const POV_OPTIONS = [
  { value: "", label: "Select..." },
  { value: "we_our", label: "We / Our" },
  { value: "company_team", label: "The company / The team" },
  { value: "you_your", label: "You / Your focused" },
];

const WRITING_MODEL_OPTIONS = [
  { value: "claude-opus-4-8", label: "Claude Opus 4.8 (default, highest quality)" },
  { value: "claude-opus-4-7", label: "Claude Opus 4.7" },
  { value: "claude-opus-4-6", label: "Claude Opus 4.6" },
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (faster, cheaper)" },
];

const REGION_LANGUAGE_OPTIONS = [
  { value: "English (US)", label: "English (US)" },
  { value: "English (UK)", label: "English (UK)" },
  { value: "English (CA)", label: "English (Canada)" },
  { value: "English (AU)", label: "English (Australia)" },
];

// Prompt Customization's "Default writing model" includes a Platform default.
const PROMPT_MODEL_OPTIONS = [
  { value: "claude-opus-4-8", label: "Platform default" },
  { value: "claude-opus-4-7", label: "Claude Opus 4.7" },
  { value: "claude-opus-4-6", label: "Claude Opus 4.6" },
  { value: "claude-sonnet-4-6", label: "Claude Sonnet" },
];

const VIDEO_VOICEOVER_OPTIONS = [
  { value: "adam_male", label: "Adam — Male (default)" },
  { value: "rachel_female", label: "Rachel — Female" },
  { value: "antoni_male", label: "Antoni — Male" },
  { value: "bella_female", label: "Bella — Female" },
];

export default function ClientSettingsTab() {
  const { client, refreshClient } = useOutletContext<WorkspaceContext>();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(() => clientToFormState(client));
  const [originalForm, setOriginalForm] = useState<FormState>(() =>
    clientToFormState(client)
  );
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<
    { kind: "success" | "error"; message: string } | null
  >(null);

  // Auto-Fill state
  const [autoFilling, setAutoFilling] = useState(false);

  // GBP Parse state
  const [gbpParseText, setGbpParseText] = useState("");
  const [parsingGbp, setParsingGbp] = useState(false);
  const [gbpParseError, setGbpParseError] = useState<string | null>(null);

  // GBP Compare state (fix559). "Parse + Compare" parses the pasted text and
  // diffs it against what's currently saved in this section, showing what would
  // change BEFORE anything is overwritten. `gbpDiff` holds the computed diff and
  // the parsed payload; applying it runs the same overwrite as Parse + Add.
  const [comparingGbp, setComparingGbp] = useState(false);
  const [gbpDiff, setGbpDiff] = useState<GbpDiff | null>(null);

  // Target Audience autofill state (chunk 3q)
  const [autoFillingTA, setAutoFillingTA] = useState(false);
  // Per-section "AI Fill this section" state (chunk3z-fix85). Holds the id of
  // the section currently filling, so each button can show its own spinner.
  const [fillingSection, setFillingSection] = useState<string | null>(null);
  // fix279: the Brand & CTA nav dot stays grey until the user MANUALLY edits a
  // field in that section this session. AI-fill (which writes via setForm, not
  // update()) does NOT count. Reset on every client load so it starts grey.
  const [brandCtaTouched, setBrandCtaTouched] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const fresh = clientToFormState(client);
    setForm(fresh);
    setOriginalForm(fresh);
    // fix279: start the Brand & CTA dot grey on every load (session-based).
    setBrandCtaTouched(false);
  }, [client]);

  // Auto-dismiss the "Settings saved." banner after 5s so it doesn't linger
  // (or follow the user across tabs). Errors stay until the next action.
  useEffect(() => {
    if (saveStatus?.kind !== "success") return;
    const t = setTimeout(() => setSaveStatus(null), 5000);
    return () => clearTimeout(t);
  }, [saveStatus]);

  const score = useMemo(() => calculateQualityScore(form), [form]);
  const missingRequired = useMemo(() => findMissingRequiredFields(form), [form]);
  const scoreBand = useMemo(
    () => getScoreBand(score, missingRequired),
    [score, missingRequired]
  );
  // fix269: highest-impact incomplete fields for the "To reach 100%" panel.
  const scoreGaps = useMemo(() => getScoreGaps(form), [form]);

  const isDirty = useMemo(
    () => JSON.stringify(serializableForm(form)) !== JSON.stringify(serializableForm(originalForm)),
    [form, originalForm]
  );

  // fix260: expose the current dirty state to the workspace tab-nav so it can
  // warn before navigating away (which would unmount this tab and lose edits).
  // We register a stable checker that reads the latest isDirty via a ref.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  useEffect(() => {
    const checker = () => isDirtyRef.current;
    registerUnsavedGuard(checker);
    return () => clearUnsavedGuard(checker);
  }, []);

  // Also catch full-page close/refresh while dirty.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // chunk3z-fix77/79: sidebar nav + single-panel switcher. Sections in the
  // reference's order, each with a completion dot. (activeSection / contentRef /
  // panelFor are declared below.)
  const navSections: SettingsNavSection[] = useMemo(() => {
    const filled = (v: unknown) => isFieldFilled(v) !== "empty";
    return [
      {
        id: "business-information",
        label: "Business Information",
        state:
          filled(form.businessName) && filled(form.websiteUrl) && filled(form.businessAddress)
            ? "full"
            : filled(form.businessName)
              ? "partial"
              : "empty",
      },
      {
        id: "about-business",
        label: "About Business",
        state: filled(form.aboutBusiness) ? (filled(form.uniqueSellingPoints) ? "full" : "partial") : "empty",
      },
      {
        id: "voice-tone",
        label: "Voice & Tone",
        state: form.voiceTones.length > 0 ? "full" : "empty",
      },
      {
        id: "target-audience",
        label: "Target Audience",
        state: filled(form.targetAudience) ? "full" : "empty",
      },
      {
        id: "terminology",
        label: "Terminology",
        state:
          filled(form.wordsToAlwaysUse) || filled(form.wordsToNeverUse) ? "full" : "empty",
      },
      {
        id: "brand-cta",
        label: "Brand & CTA",
        // fix279: grey until the user manually edits this section this session.
        // AI-fill does not flip it (brandCtaTouched is only set by update()).
        state: !brandCtaTouched
          ? "empty"
          : filled(form.heroTagline) && filled(form.ctaText)
            ? "full"
            : filled(form.heroTagline) ||
                filled(form.ctaText) ||
                filled(form.brandPrimaryColor)
              ? "partial"
              : "empty",
      },
      {
        id: "local-details",
        label: "Local Details",
        state: filled(form.localDetails) ? "full" : "empty",
      },
      {
        id: "credentials",
        label: "Credentials",
        state: filled(form.credentials) ? "full" : "empty",
      },
      {
        id: "specific-requests",
        label: "Specific Requests",
        state: filled(form.specificRequests) ? "full" : "empty",
      },
      {
        id: "external-links",
        label: "External Links",
        state: form.externalLinkDomains.size > 0 ? "full" : "empty",
      },
      {
        id: "google-business-profile",
        label: "Google Business Profile",
        state:
          filled(form.gbpPrimaryCategory) && filled(form.gbpServicesText)
            ? "full"
            : filled(form.gbpPrimaryCategory)
              ? "partial"
              : "empty",
      },
      {
        id: "customer-reviews",
        label: "Customer Reviews",
        state: filled(form.averageRating) ? "full" : "empty",
      },
      {
        id: "image-preferences",
        label: "Image Preferences",
        state: filled(form.brandStyleGuide) ? "full" : "empty",
      },
      {
        id: "youtube-video",
        label: "YouTube / Video",
        state: "empty",
      },
      {
        id: "prompt-customization",
        label: "Prompt Customization",
        state:
          filled(form.customContentPrompt) ||
          filled(form.imageStyleNotes) ||
          (form.writingModel && form.writingModel !== "claude-opus-4-8")
            ? "full"
            : "empty",
      },
    ];
  }, [form, brandCtaTouched]);

  // chunk3z-fix79: single-panel switcher. Clicking a sidebar item shows ONLY
  // that section's panel (matching the reference), instead of scrolling a long
  // stack. `activeSection` selects which panel renders. Some panels answer to
  // more than one nav id (e.g. Voice & Tone also backs the Terminology link,
  // About also backs Credentials) — see `panelFor`.
  // fix267: persist the active settings section in the URL (?section=...) so a
  // page reload restores the section you were on instead of resetting to
  // Business Information. The param is validated against the known section IDs.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_SECTION_IDS = useMemo(
    () =>
      new Set<string>([
        "business-information",
        "about-business",
        "voice-tone",
        "target-audience",
        "terminology",
        "brand-cta",
        "local-details",
        "credentials",
        "specific-requests",
        "external-links",
        "google-business-profile",
        "customer-reviews",
        "image-preferences",
        "youtube-video",
        "prompt-customization",
      ]),
    []
  );
  const initialSection = (() => {
    const fromUrl = searchParams.get("section");
    return fromUrl && VALID_SECTION_IDS.has(fromUrl)
      ? fromUrl
      : "business-information";
  })();
  const [activeSection, setActiveSection] = useState<string>(initialSection);
  const contentRef = useRef<HTMLDivElement>(null);

  // Map a nav id to the panel id that actually renders its content.
  // (chunk3z-fix84: Terminology and Credentials now have their own panels to
  // match the Core 30 reference — no more aliasing onto Voice/About.)
  function panelFor(navId: string): string {
    return navId;
  }
  const activePanel = panelFor(activeSection);
  const show = (panelId: string) => activePanel === panelId;

  function scrollToSection(id: string) {
    setActiveSection(id);
    // fix267: reflect the section in the URL so reload restores it. replace:true
    // avoids polluting browser history with a back-entry per section click.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("section", id);
        return next;
      },
      { replace: true }
    );
    // chunk3z-fix527: jump to the top when switching sections. The content div
    // (contentRef) isn't itself scrollable — the whole window scrolls (layout is
    // min-h-screen with no inner overflow container) — so scrolling the ref did
    // nothing and the new section opened wherever the previous scroll position
    // happened to be. Scroll the window to the top so every section starts from
    // its heading. Keep the ref scroll too as a harmless fallback in case a
    // future layout introduces an inner scroll container.
    window.scrollTo({ top: 0, behavior: "auto" });
    contentRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }

  // fix279: fields that live in the Brand & CTA section. A manual edit to any of
  // these (via update(), NOT via AI-fill's setForm) marks the section "touched".
  const BRAND_CTA_FIELDS = new Set<keyof FormState>([
    "brandPrimaryColor",
    "heroTagline",
    "ctaText",
  ]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaveStatus(null);
    if (BRAND_CTA_FIELDS.has(key)) setBrandCtaTouched(true);
  }

  async function handleSave() {
    setSaving(true);
    setSaveStatus(null);
    try {
      const payload = formStateToUpdatePayload(form);
      await updateClient(client.id, payload);
      await refreshClient();
      setSaveStatus({ kind: "success", message: "Settings saved." });
    } catch (err) {
      setSaveStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Failed to save",
      });
    } finally {
      setSaving(false);
    }
  }

  // ============================================================
  // Auto-Fill from Website
  // Calls /api/clients/:id/autofill-master which extracts business data
  // from the homepage + best-effort About page via Opus.
  // GBP fields (Primary, Secondary, Services) are NOT touched here —
  // those use the Paste-GBP textarea workflow.
  // Only fills empty fields; never overwrites.
  async function handleAutoFill() {
    if (!form.websiteUrl) {
      alert("Add a Website URL first, then run Auto-Fill.");
      return;
    }
    setAutoFilling(true);
    try {
      const result = await masterAutofill(client.id);
      const extracted = result.data;

      // Walk every extractable field. Only apply if the form field is
      // currently empty — never overwrite existing user content. Track the
      // labels of what got filled (for the toast) and what was extracted
      // but skipped because the field already had content.
      const filledLabels: string[] = [];
      const skippedAlreadyFilled: string[] = [];
      const updates: Partial<FormState> = {};

      const considerString = (
        key: keyof FormState,
        label: string,
        extractedValue: string | null
      ) => {
        if (!extractedValue) return;
        const current = form[key];
        if (typeof current === "string" && current.trim().length > 0) {
          skippedAlreadyFilled.push(label);
          return;
        }
        (updates as Record<string, unknown>)[key as string] = extractedValue;
        filledLabels.push(label);
      };

      considerString(
        "businessAddress",
        "Business Address",
        extracted.businessAddress
          ? formatAddressTwoLines(extracted.businessAddress)
          : extracted.businessAddress
      );
      considerString("phoneNumber", "Phone Number", extracted.phoneNumber);
      considerString("aboutBusiness", "About", extracted.aboutBusiness);
      considerString("uniqueSellingPoints", "USPs", extracted.uniqueSellingPoints);
      considerString("credentials", "Credentials", extracted.credentials);
      // fix257: the master "fill what's missing" now also fills the Writing
      // Voice Profile (it previously skipped it entirely).
      considerString(
        "writingVoiceProfile",
        "Writing Voice Profile",
        extracted.writingVoiceProfile
      );
      considerString("wordsToAlwaysUse", "Words to Always Use", extracted.wordsToAlwaysUse);
      considerString("ctaText", "CTA Text", extracted.ctaText);
      considerString("socialProfiles", "Social Profiles", extracted.socialProfiles);
      considerString("businessLogoUrl", "Logo", extracted.businessLogoUrl);
      considerString("pointOfView", "Point of View", extracted.pointOfView);
      // NOTE: GBP fields (Primary Category, Secondary Categories, Services)
      // are NOT filled by this button. Use the Paste-GBP textarea below —
      // it parses what's actually in the GBP dashboard.
      // fix259: Target Audience IS now filled by this button (see the dedicated
      // step further down — it uses the address + GBP services endpoint).

      if (
        extracted.voiceTones &&
        extracted.voiceTones.length > 0 &&
        form.voiceTones.length === 0
      ) {
        updates.voiceTones = extracted.voiceTones;
        filledLabels.push("Voice Tones");
      } else if (extracted.voiceTones && extracted.voiceTones.length > 0) {
        skippedAlreadyFilled.push("Voice Tones");
      }

      if (extracted.averageRating != null && !form.averageRating) {
        updates.averageRating = String(extracted.averageRating);
        filledLabels.push("Average Rating");
      } else if (extracted.averageRating != null) {
        skippedAlreadyFilled.push("Average Rating");
      }

      if (extracted.totalReviewCount != null && !form.totalReviewCount) {
        updates.totalReviewCount = String(extracted.totalReviewCount);
        filledLabels.push("Total Review Count");
      } else if (extracted.totalReviewCount != null) {
        skippedAlreadyFilled.push("Total Review Count");
      }

      // fix259: the master button now ALSO fills Target Audience (previously it
      // had to be done via its own dedicated button). It uses a separate
      // endpoint driven by the practice address + GBP services, so we run it as
      // its own step. Only-fill-if-empty, and only if we have enough address
      // info (city+state OR zip). Uses the address we may have JUST extracted in
      // this same run, falling back to whatever is already in the form.
      if (!form.targetAudience || form.targetAudience.trim().length === 0) {
        const effectiveAddress =
          (updates.businessAddress as string | undefined) ?? form.businessAddress;
        const { city, state, zip } = parseAddressLocality(effectiveAddress || "");
        const hasCityState = city.length > 0 && state.length > 0;
        const hasZip = zip.length > 0;
        if (hasCityState || hasZip) {
          try {
            const { targetAudience } = await autofillTargetAudience(client.id, {
              practiceAddress: effectiveAddress,
              city,
              state,
              zip,
              services: form.gbpServicesText,
            });
            if (targetAudience && targetAudience.trim().length > 0) {
              updates.targetAudience = targetAudience;
              filledLabels.push("Target Audience");
            }
          } catch {
            // Non-fatal — the rest of the master fill should still apply.
          }
        }
      } else {
        skippedAlreadyFilled.push("Target Audience");
      }

      // fix260: the master button now ALSO fills Local Details (previously only
      // its dedicated "Auto-generate from address" button did). Same address-
      // driven endpoint and same only-fill-if-empty + address-gate logic.
      if (!form.localDetails || form.localDetails.trim().length === 0) {
        const effectiveAddress =
          (updates.businessAddress as string | undefined) ?? form.businessAddress;
        const { city, state, zip } = parseAddressLocality(effectiveAddress || "");
        const hasCityState = city.length > 0 && state.length > 0;
        const hasZip = zip.length > 0;
        if (hasCityState || hasZip) {
          try {
            const { localDetails } = await autofillLocalDetails(client.id, {
              practiceAddress: effectiveAddress,
              city,
              state,
              zip,
              services: form.gbpServicesText,
            });
            if (localDetails && localDetails.trim().length > 0) {
              updates.localDetails = localDetails;
              filledLabels.push("Local Details");
            }
          } catch {
            // Non-fatal — the rest of the master fill should still apply.
          }
        }
      } else {
        skippedAlreadyFilled.push("Local Details");
      }

      // Empty-result path: nothing was extracted that could fill an empty
      // field. Surface what the website extraction did so the user can
      // diagnose.
      if (filledLabels.length === 0) {
        const wExt = result.websiteExtraction;
        const lines: string[] = [];
        if (wExt.status === "failed") {
          lines.push(`• Website extraction failed: ${wExt.message}`);
        } else if (skippedAlreadyFilled.length > 0) {
          lines.push(
            `• Website extraction OK — found ${skippedAlreadyFilled.length} field(s) but they're already filled: ${skippedAlreadyFilled.join(", ")}`
          );
        } else {
          lines.push(`• Website extraction OK — but returned no useful data.`);
        }
        alert(
          "Auto-Fill found nothing new to fill.\n\n" +
            lines.join("\n") +
            "\n\nIf the website extraction failed, check the URL is reachable and try again." +
            "\n\nFor GBP categories and services, paste them from your GBP dashboard into the Paste-GBP textarea below."
        );
        setAutoFilling(false);
        return;
      }

      // Apply directly — no preview modal. The user explicitly clicked the
      // button knowing what it does, and only-fill-empty-fields makes this
      // non-destructive.
      setForm((prev) => ({ ...prev, ...updates }));
      setSaveStatus(null);

      // Build a result toast: what filled, what was skipped, any failure.
      const lines: string[] = [];
      lines.push(`Filled ${filledLabels.length} field${filledLabels.length === 1 ? "" : "s"}: ${filledLabels.join(", ")}`);
      if (skippedAlreadyFilled.length > 0) {
        lines.push(`Skipped (already filled): ${skippedAlreadyFilled.join(", ")}`);
      }
      const wExt = result.websiteExtraction;
      if (wExt.status === "failed") {
        lines.push(`Website extraction failed: ${wExt.message}`);
      }
      lines.push("For GBP categories and services, paste from your GBP dashboard below.");
      const hasFailure = wExt.status === "failed";
      toast.show(lines.join("\n"), {
        kind: hasFailure ? "warning" : "success",
      });
    } catch (err) {
      alert(formatErrorForAlert(err, "Auto-Fill failed"));
    } finally {
      setAutoFilling(false);
    }
  }

  // ============================================================
  // GBP Parser
  // ============================================================

  // Turn a ParsedGBP payload into the FormState updates + a human summary of
  // what got written. Shared by Parse + Add and Parse + Compare's "Apply".
  function buildGbpUpdates(parsed: ParsedGBP): {
    updates: Partial<FormState>;
    updated: string[];
  } {
    const updates: Partial<FormState> = {};
    const updated: string[] = [];

    if (parsed.primaryCategory) {
      updates.gbpPrimaryCategory = parsed.primaryCategory;
      updated.push(`Primary Category → "${parsed.primaryCategory}"`);
    }
    if (parsed.secondaryCategories.length > 0) {
      updates.gbpSecondaryCategoriesText = parsed.secondaryCategories.join(", ");
      updated.push(
        `Secondary Categories → ${parsed.secondaryCategories.length} ` +
          `(${parsed.secondaryCategories.join(", ")})`
      );
    }
    if (Object.keys(parsed.services).length > 0) {
      // Write in the new format (CATEGORY uppercase + plain lines).
      // Parser still accepts the old "### + -" format if anyone pastes it.
      const lines: string[] = [];
      let totalServices = 0;
      for (const [cat, services] of Object.entries(parsed.services)) {
        lines.push(cat.toUpperCase());
        for (const s of services) lines.push(s);
        lines.push("");
        totalServices += services.length;
      }
      updates.gbpServicesText = lines.join("\n").trim();
      updated.push(
        `Services → ${totalServices} service(s) across ${Object.keys(parsed.services).length} categor(y/ies)`
      );
    }
    return { updates, updated };
  }

  // Apply a parsed GBP payload to the form (the overwrite). Returns false and
  // sets an error if the payload was empty.
  function applyParsedGbp(parsed: ParsedGBP): boolean {
    const { updates, updated } = buildGbpUpdates(parsed);
    if (updated.length === 0) {
      setGbpParseError(
        "Parser couldn't find a Primary Category, Secondary Categories, " +
          "or Services in the pasted text. Try rephrasing or pasting more " +
          "context."
      );
      return false;
    }
    setForm((prev) => ({ ...prev, ...updates }));
    setSaveStatus(null);
    toast.show(`GBP Parser updated:\n• ${updated.join("\n• ")}`, {
      kind: "success",
    });
    return true;
  }

  async function handleParseGBP() {
    if (!gbpParseText.trim()) return;
    setParsingGbp(true);
    setGbpParseError(null);
    setGbpDiff(null);
    try {
      const { parsed } = await parseGBP(gbpParseText);
      // Keep gbpParseText so the user can review what they pasted.
      applyParsedGbp(parsed);
    } catch (err) {
      setGbpParseError(formatErrorForAlert(err, "Parse failed"));
    } finally {
      setParsingGbp(false);
    }
  }

  // Parse + Compare (fix559): parse the pasted text, diff against what's
  // currently in the form, and show the diff for review before overwriting.
  async function handleCompareGBP() {
    if (!gbpParseText.trim()) return;
    setComparingGbp(true);
    setGbpParseError(null);
    setGbpDiff(null);
    try {
      const { parsed } = await parseGBP(gbpParseText);
      const current: ParsedGBP = {
        primaryCategory: form.gbpPrimaryCategory.trim() || null,
        secondaryCategories: form.gbpSecondaryCategoriesText
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        services: parseGbpServicesText(form.gbpServicesText),
      };
      const diff = computeGbpDiff(current, parsed);
      if (!diff.hasAnyContent) {
        setGbpParseError(
          "Parser couldn't find a Primary Category, Secondary Categories, " +
            "or Services in the pasted text. Try rephrasing or pasting more " +
            "context."
        );
        return;
      }
      setGbpDiff(diff);
    } catch (err) {
      setGbpParseError(formatErrorForAlert(err, "Compare failed"));
    } finally {
      setComparingGbp(false);
    }
  }

  // Apply the currently-shown diff (same overwrite as Parse + Add).
  function applyGbpDiff() {
    if (!gbpDiff) return;
    if (applyParsedGbp(gbpDiff.parsed)) {
      setGbpDiff(null);
    }
  }


  /**
   * Target Audience auto-fill (chunk 3q item #1).
   *
   * Validation gate: must have city+state OR zip. Parsed from the freeform
   * Business Address field (format: "123 Main St, City, ST ZIP").
   * Services line uses the GBP Services textarea (any format) if non-empty;
   * otherwise the prompt itself falls back to "assume typical general dentistry."
   */
  async function handleAutoFillTargetAudience() {
    if (autoFillingTA) return;
    const { city, state, zip } = parseAddressLocality(form.businessAddress);

    const hasCityState = city.length > 0 && state.length > 0;
    const hasZip = zip.length > 0;
    if (!hasCityState && !hasZip) {
      toast.show("Add at least City + State or ZIP before auto-filling.", {
        kind: "warning",
      });
      return;
    }

    setAutoFillingTA(true);
    try {
      const { targetAudience } = await autofillTargetAudience(client.id, {
        practiceAddress: form.businessAddress,
        city,
        state,
        zip,
        services: form.gbpServicesText,
      });
      setForm((prev) => ({ ...prev, targetAudience }));
      setSaveStatus(null);
      toast.show("Target audience filled in. Review and Save.", {
        kind: "success",
      });
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Auto-fill failed"), {
        kind: "error",
      });
    } finally {
      setAutoFillingTA(false);
    }
  }

  /**
   * Local Details auto-generate (chunk3z-fix85). Same address gate as Target
   * Audience; calls the dedicated local-details endpoint and overwrites the
   * Local Details field.
   */
  async function handleAutoFillLocalDetails() {
    if (fillingSection) return;
    const { city, state, zip } = parseAddressLocality(form.businessAddress);
    const hasCityState = city.length > 0 && state.length > 0;
    const hasZip = zip.length > 0;
    if (!hasCityState && !hasZip) {
      toast.show("Add at least City + State or ZIP in Business Address first.", {
        kind: "warning",
      });
      return;
    }
    setFillingSection("local-details");
    try {
      const { localDetails } = await autofillLocalDetails(client.id, {
        practiceAddress: form.businessAddress,
        city,
        state,
        zip,
        services: form.gbpServicesText,
      });
      setForm((prev) => ({ ...prev, localDetails }));
      setSaveStatus(null);
      toast.show("Local details generated. Review and Save.", { kind: "success" });
    } catch (err) {
      toast.show(formatErrorForAlert(err, "Auto-generate failed"), { kind: "error" });
    } finally {
      setFillingSection(null);
    }
  }

  /**
   * Per-section "AI Fill this section" (chunk3z-fix85).
   *
   * Reuses the master website extraction, then applies ONLY the fields that
   * belong to the named section. Unlike the master "fill what's missing"
   * button, this OVERWRITES the section's fields — the user explicitly asked to
   * (re)fill that one section. Target Audience and Local Details have their own
   * dedicated endpoints and are routed there.
   */
  async function handleAiFillSection(section: string) {
    if (fillingSection) return;

    if (section === "target-audience") {
      setFillingSection("target-audience");
      try {
        await handleAutoFillTargetAudience();
      } finally {
        setFillingSection(null);
      }
      return;
    }
    if (section === "local-details") {
      await handleAutoFillLocalDetails();
      return;
    }

    if (!form.websiteUrl) {
      toast.show("Add a Website URL first, then use AI Fill.", { kind: "warning" });
      return;
    }

    setFillingSection(section);
    try {
      const result = await masterAutofill(client.id);
      const x = result.data;
      const updates: Partial<FormState> = {};
      let any = false;
      const set = (key: keyof FormState, val: string | null | undefined) => {
        if (val == null || String(val).trim().length === 0) return;
        (updates as Record<string, unknown>)[key as string] = val;
        any = true;
      };

      switch (section) {
        case "business-information":
          set("businessAddress", x.businessAddress);
          set("phoneNumber", x.phoneNumber);
          set("socialProfiles", x.socialProfiles);
          break;
        case "about-business":
          set("aboutBusiness", x.aboutBusiness);
          set("uniqueSellingPoints", x.uniqueSellingPoints);
          break;
        case "voice-tone":
          if (x.voiceTones && x.voiceTones.length > 0) {
            updates.voiceTones = x.voiceTones;
            any = true;
          }
          set("pointOfView", x.pointOfView);
          // fix257: fill the Writing Voice Profile from the dedicated
          // style-analysis field — NOT wordsToAlwaysUse, which is a tagline/
          // phrases field and produced the title-echo bug.
          set("writingVoiceProfile", x.writingVoiceProfile);
          break;
        case "terminology":
          set("wordsToAlwaysUse", x.wordsToAlwaysUse);
          break;
        case "credentials":
          set("credentials", x.credentials);
          break;
        default:
          break;
      }

      if (!any) {
        const wExt = result.websiteExtraction;
        toast.show(
          wExt.status === "failed"
            ? `Website extraction failed: ${wExt.message}`
            : "Nothing found to fill for this section from the website.",
          { kind: "warning" }
        );
        return;
      }

      setForm((prev) => ({ ...prev, ...updates }));
      setSaveStatus(null);
      toast.show("Section filled in. Review and Save.", { kind: "success" });
    } catch (err) {
      toast.show(formatErrorForAlert(err, "AI Fill failed"), { kind: "error" });
    } finally {
      setFillingSection(null);
    }
  }

  /** Small reusable "AI Fill this section" button for a Section's rightSlot. */
  function aiFillButton(section: string) {
    const busy = fillingSection === section;
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={() => handleAiFillSection(section)}
        disabled={fillingSection != null}
      >
        {busy ? "Filling…" : "✨ AI Fill this section"}
      </Button>
    );
  }

  /** Sidebar "AI fill what's missing" — master website extraction, only-fill-
   * empty semantics (delegates to handleAutoFill). */
  async function handleAiFillMissing() {
    if (fillingSection) return;
    setFillingSection("__missing__");
    try {
      await handleAutoFill();
    } finally {
      setFillingSection(null);
    }
  }

  return (
    <div className="flex gap-6 items-start">
      <SettingsSidebar
        sections={navSections}
        activeId={activeSection}
        onNavigate={scrollToSection}
        score={score}
        scoreColor={scoreBand.barColorRgb}
        gaps={scoreGaps}
        onGapClick={(gapKey) => scrollToSection(gapSectionFor(gapKey))}
        onAiFillMissing={handleAiFillMissing}
        aiFillMissingBusy={fillingSection === "__missing__"}
      />

      <div ref={contentRef} className="flex-1 min-w-0 max-w-4xl">
      {/* SECTION 1: Business Information */}
      {show("business-information") && (
      <div>
      <Section title="Business Information" subtitle="Required" collapsible={false} rightSlot={aiFillButton("business-information")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Business Name" required>
            <TextInput
              value={form.businessName}
              onChange={(v) => update("businessName", v)}
            />
          </Field>
          <Field label="Website URL" required>
            <TextInput
              type="url"
              value={form.websiteUrl}
              onChange={(v) => update("websiteUrl", v)}
            />
          </Field>
        </div>

        <Field
          label="Business Address"
          required
          hint="Full street address including city and state — used in local SEO content."
        >
          <Textarea
            value={form.businessAddress}
            onChange={(v) => update("businessAddress", v)}
            onBlur={() => {
              // fix256: reflow single-line address to two lines (break at city).
              const reflowed = formatAddressTwoLines(form.businessAddress);
              if (reflowed !== form.businessAddress) {
                update("businessAddress", reflowed);
              }
            }}
            placeholder={"10844 Providence Rd #270\nCharlotte, NC 28277"}
            rows={2}
          />
        </Field>

        <Field
          label="Region / Language"
          hint="Language and regional variant for all generated content — spelling, grammar, idioms, and structured data. Affects DataForSEO SERP research too."
        >
          <Select
            value={form.regionLanguage}
            onChange={(v) => update("regionLanguage", v)}
            options={REGION_LANGUAGE_OPTIONS}
          />
          {form.regionLanguage && (
            <p className="text-success text-xs mt-1">
              ✓ SERP research will use {form.regionLanguage}
            </p>
          )}
        </Field>

        <Field label="Phone Number">
          <TextInput
            value={form.phoneNumber}
            onChange={(v) => update("phoneNumber", v)}
            placeholder="(704) 396-4767"
          />
        </Field>

        <Field
          label="GBP Primary Category"
          required
          hint="Your main Google Business Profile category — drives schema type and content tone."
        >
          <TextInput
            value={form.gbpPrimaryCategory}
            onChange={(v) => update("gbpPrimaryCategory", v)}
            placeholder="e.g. Dentist"
          />
        </Field>

        <Field label="Logo URL">
          <LogoUrlInput
            value={form.businessLogoUrl}
            onChange={(v) => update("businessLogoUrl", v)}
          />
        </Field>

        <Field label="Social Profiles" hint="One URL per line.">
          <Textarea
            value={form.socialProfiles}
            onChange={(v) => update("socialProfiles", v)}
            placeholder={"https://www.facebook.com/...\nhttps://www.instagram.com/...\nhttps://www.youtube.com/@..."}
            rows={3}
          />
        </Field>
      </Section>
      </div>
      )}

      {/* SECTION 2: Google Business Profile */}
      {show("google-business-profile") && (
      <div>
      <Section
        title="Google Business Profile"
        subtitle="Special setup"
        collapsible={false}
      >
        {/* GBP tab cross-link card (matches reference) */}
        <div className="flex items-start justify-between gap-3 bg-bg-subtle border border-border rounded-lg px-4 py-3 mb-4">
          <div>
            <p className="text-text font-medium text-sm">Google Business Profile</p>
            <p className="text-text-subtle text-xs mt-0.5">
              Category audit and competitor analysis live in the GBP tab.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("../gbp")}
          >
            Open GBP tab →
          </Button>
        </div>

        {/* Master Auto-Fill button (chunk 3y) — pulls Address, GBP categories,
            About, USPs, Credentials, etc. from the website + DataForSEO Local
            Pack. Only fills empty fields. */}
        <div className="mb-4 flex items-start gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAutoFill}
            disabled={autoFilling}
          >
            {autoFilling ? "Reading website..." : "✨ Auto-Fill from Website"}
          </Button>
          <p className="text-text-subtle text-xs mt-1.5 flex-1">
            Reads {form.websiteUrl || "the website"} (homepage + about page) and
            fills About, USPs, Credentials, Voice Tones, POV, CTA, Social,
            Ratings — only if those fields are empty.
          </p>
        </div>

        <Field
          label="Paste GBP Categories & Services"
          hint="Paste your GBP categories & services to insert them. Then the parser works out which services belong to each category in the grouped view below. Re-pasting re-imports and overwrites the whole mapping, including any manual edits."
        >
          {/* Warning banner — matches reference */}
          <div className="flex gap-2 bg-warning/10 border border-warning/40 rounded-lg px-3 py-2 mb-2">
            <span className="text-warning text-sm leading-none mt-0.5">⚠</span>
            <p className="text-text-muted text-xs">
              Enter your optimized categories and services — not necessarily
              what you currently have. Writing content for the wrong categories
              is worse than writing no content at all. Verify this is correct
              before generating any pages. You can write content before the GBP
              is updated.
            </p>
          </div>
          <Textarea
            value={gbpParseText}
            onChange={setGbpParseText}
            placeholder="Paste categories & services from any source — the GBP Services panel on your phone, the GBP dashboard, a spreadsheet, competitor tools, or a plain list. Descriptions are fine; the parser strips them out..."
            rows={3}
          />
          <div className="flex items-center gap-2 mt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleParseGBP}
              disabled={parsingGbp || comparingGbp || !gbpParseText.trim()}
            >
              {parsingGbp ? "Parsing..." : "Parse + Add"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCompareGBP}
              disabled={parsingGbp || comparingGbp || !gbpParseText.trim()}
            >
              {comparingGbp ? "Comparing..." : "Parse + Compare"}
            </Button>
            {gbpParseError && (
              <span className="text-danger text-xs">{gbpParseError}</span>
            )}
            <span className="text-text-subtle text-xs">
              Add replaces the fields below; Compare shows what changed first.
            </span>
          </div>

          {/* Compare results (fix559) — a read-only diff of the pasted list vs.
              what's currently saved. Nothing is written until "Apply changes". */}
          {gbpDiff && (
            <GbpDiffPanel
              diff={gbpDiff}
              onApply={applyGbpDiff}
              onDismiss={() => setGbpDiff(null)}
            />
          )}
          {/* Synced status line — matches reference's "✓ Profile data synced" */}
          {form.gbpPrimaryCategory.trim() && (
            <p className="text-success text-xs mt-2">
              ✓ Profile data synced — {form.gbpPrimaryCategory.trim()}
            </p>
          )}
        </Field>

        <Field label="Primary Category" hint="Your main Google Business Profile category — drives schema type and content tone.">
          <TextInput
            value={form.gbpPrimaryCategory}
            onChange={(v) => update("gbpPrimaryCategory", v)}
            placeholder="e.g. Dentist"
          />
        </Field>

        <Field
          label="Secondary Categories"
          hint="One chip per category — pulled from GBP sync or edit manually."
        >
          <ChipTextField
            value={form.gbpSecondaryCategoriesText}
            onChange={(v) => update("gbpSecondaryCategoriesText", v)}
            placeholder="Add category…"
          />
        </Field>

        <Field
          label="GBP Services"
          hint="Manage your full service list and which category each belongs to. The grouped view controls what's used for category pages."
        >
          <GbpServicesEditor
            value={form.gbpServicesText}
            onChange={(v) => update("gbpServicesText", v)}
          />
        </Field>
      </Section>
      </div>
      )}

      {/* SECTION 3: About the Business */}
      {show("about-business") && (
      <div>
      <Section title="About the Business" subtitle="Required" collapsible={false} rightSlot={aiFillButton("about-business")}>
        <Field
          label="Tell us about this business"
          required
          hint="What the business does, history, mission, approach. Highest-leverage field for content quality."
        >
          <Textarea
            value={form.aboutBusiness}
            onChange={(v) => update("aboutBusiness", v)}
            placeholder="What the business does, its history, mission, and approach..."
            rows={5}
          />
        </Field>

        <Field
          label="Unique Selling Points"
          hint="What sets this business apart? Bullet points or sentences."
        >
          <Textarea
            value={form.uniqueSellingPoints}
            onChange={(v) => update("uniqueSellingPoints", v)}
            placeholder="What differentiates them from competitors..."
            rows={4}
          />
        </Field>
      </Section>
      </div>
      )}

      {/* SECTION 4: Voice & Tone */}
      {show("voice-tone") && (
      <div>
      <Section title="Voice & Tone" subtitle="Required" collapsible={false} rightSlot={aiFillButton("voice-tone")}>
        <Field
          label="Writing Voice Profile"
          hint="The single biggest factor in content quality — describe how the business talks, what to emphasize, what to avoid. Aim for 50+ words."
        >
          <Textarea
            value={form.writingVoiceProfile}
            onChange={(v) => update("writingVoiceProfile", v)}
            placeholder="Write like a warm, reassuring neighbor who happens to be a dental expert. Calm and confident, never clinical or cold. Speak directly to the reader (&quot;you,&quot; &quot;your smile&quot;) and use &quot;we&quot; and &quot;our team&quot; to show there are real people behind the care..."
            rows={5}
          />
        </Field>

        <Field label="Tone (pick up to 2)" required>
          <TagPicker
            options={TONE_OPTIONS}
            selected={form.voiceTones}
            onChange={(v) => update("voiceTones", v)}
            max={2}
          />
        </Field>

        <Field label="Point of View" required>
          <Select
            value={form.pointOfView}
            onChange={(v) => update("pointOfView", v)}
            options={POV_OPTIONS}
          />
        </Field>

        <Field
          label="Writing Samples"
          hint="Paste any existing content that captures the voice you want to match."
        >
          <Textarea
            value={form.writingSamples}
            onChange={(v) => update("writingSamples", v)}
            placeholder="Paste existing website copy, emails, or marketing text here..."
            rows={4}
          />
        </Field>
      </Section>
      </div>
      )}

      {/* SECTION 4b: Terminology */}
      {show("terminology") && (
      <div>
      <Section title="Terminology" subtitle="Recommended" collapsible={false} rightSlot={aiFillButton("terminology")}>
        <Field
          label="Terminology to Use"
          hint="Recurring brand phrases and preferred terms, comma-separated. Aim for 3+."
        >
          <Textarea
            value={form.wordsToAlwaysUse}
            onChange={(v) => update("wordsToAlwaysUse", v)}
            placeholder="whole-home comfort, certified technician, no-surprise pricing, ..."
            rows={2}
          />
        </Field>

        <Field
          label="Terminology to Avoid"
          hint="Words and phrases to never use. Comma-separated."
        >
          <Textarea
            value={form.wordsToNeverUse}
            onChange={(v) => update("wordsToNeverUse", v)}
            placeholder="cheap, best in the city, guaranteed #1, ..."
            rows={2}
          />
        </Field>
      </Section>
      </div>
      )}

      {/* SECTION 5: Target Audience */}
      {show("target-audience") && (
      <div>
      <Section title="Target Audience" subtitle="Recommended" collapsible={false} rightSlot={aiFillButton("target-audience")}>
        <Field
          hint="Describe who the ideal customers are — demographics, needs, pain points, decision factors."
        >
          <Textarea
            value={form.targetAudience}
            onChange={(v) => update("targetAudience", v)}
            placeholder="Demographics, pain points, what they're looking for..."
            rows={4}
          />
          <p className="text-text-subtle text-xs mt-2">
            AI Fill uses the practice address and (if filled in) GBP services to
            draft a 30-50 word audience profile.
          </p>
        </Field>
      </Section>
      </div>
      )}

      {/* SECTION 5b: Credentials */}
      {show("credentials") && (
      <div>
      <Section title="Credentials" subtitle="Optional" collapsible={false} rightSlot={aiFillButton("credentials")}>
        <Field
          label="Credentials & Trust Signals"
          hint="Licenses, certifications, awards, years in business, professional memberships. These get woven into content as trust signals."
        >
          <Textarea
            value={form.credentials}
            onChange={(v) => update("credentials", v)}
            placeholder={"Doctor credentials:\nDr. Jane Smith — DDS, FAGD (Fellow, Academy of General Dentistry)..."}
            rows={6}
          />
        </Field>
      </Section>
      </div>
      )}

      {/* SECTION 6: Specific Requests */}
      {show("specific-requests") && (
      <div>
      <Section title="Specific Requests" subtitle="Optional" collapsible={false}>
        <Field
          hint="Any instructions for how content should be written. Phrases to use, topics to emphasize, things to avoid, formatting preferences."
        >
          <Textarea
            value={form.specificRequests}
            onChange={(v) => update("specificRequests", v)}
            placeholder="Always mention the free estimate. Avoid mentioning competitors by name. End every page with the phone number and a call-to-action..."
            rows={5}
          />
        </Field>
      </Section>
      </div>
      )}
      {/* brand-cta credentials: brand-cta maps to its own (hidden) panel;
          credentials maps to about-business. No standalone divs needed. */}

      {/* SECTION 7: Brand & CTA — matches Core 30 reference (colors, hero
          tagline, CTA text). Social Profiles + Logo URL live in Business
          Information per the reference. heroTagline is form-only for now. */}
      {show("brand-cta") && (
      <div>
      <Section title="Brand & CTA" subtitle="Recommended" collapsible={false}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Brand Primary Color">
            <ColorPicker
              value={form.brandPrimaryColor}
              onChange={(v) => update("brandPrimaryColor", v)}
            />
          </Field>
          <Field label="Brand Accent Color">
            <ColorPicker
              value={form.brandAccentColor}
              onChange={(v) => update("brandAccentColor", v)}
            />
          </Field>
        </div>

        <Field
          label="Hero Tagline"
          hint="One-line value proposition shown in page headers."
        >
          <TextInput
            value={form.heroTagline}
            onChange={(v) => update("heroTagline", v)}
            placeholder="Trusted family & cosmetic dentistry in Charlotte since 2009"
          />
        </Field>

        <Field
          label="CTA Text"
          hint="Primary call-to-action button label."
        >
          <TextInput
            value={form.ctaText}
            onChange={(v) => update("ctaText", v)}
            placeholder="Book Your Appointment"
          />
        </Field>
      </Section>
      </div>
      )}

      {/* SECTION 8: Page Template — HIDDEN per user request, can re-enable */}
      {false && (
      <Section title="Page Template" collapsible={false}>
        <p className="text-text-subtle text-xs mb-3">
          These settings control how generated pages are structured. They'll be applied
          to all pages this client publishes. (Page template options will be saved to the
          server in Chunk 3.)
        </p>

        <Field
          label="Hero Tagline"
          hint="Short phrase shown under the H1 on every generated page. Leave blank to let the model write it."
        >
          <TextInput
            value={form.heroTagline}
            onChange={(v) => update("heroTagline", v)}
            placeholder="e.g. Trusted by Houston families since 2003"
          />
        </Field>

        <Field
          label="CTA Buttons"
          hint="Up to 2 CTAs. Shown in the hero and footer of every page."
        >
          <RepeaterList<CTAButton>
            items={form.ctaButtons}
            onChange={(v) => update("ctaButtons", v)}
            max={2}
            blank={() => ({ text: "", url: "" })}
            addLabel="Add CTA Button"
            renderItem={(item, _i, set) => (
              <div className="grid grid-cols-2 gap-2">
                <TextInput
                  value={item.text}
                  onChange={(v) => set({ ...item, text: v })}
                  placeholder="Button text (e.g. Schedule Online)"
                />
                <TextInput
                  type="url"
                  value={item.url}
                  onChange={(v) => set({ ...item, url: v })}
                  placeholder="https:// or tel:"
                />
              </div>
            )}
          />
        </Field>

        <Field label="Trust Badges" hint="Up to 4 short proof points (e.g. '5-Star Reviews').">
          <RepeaterList<TrustBadge>
            items={form.trustBadges}
            onChange={(v) => update("trustBadges", v)}
            max={4}
            blank={() => ({ text: "" })}
            addLabel="Add Trust Badge"
            renderItem={(item, _i, set) => (
              <TextInput
                value={item.text}
                onChange={(v) => set({ text: v })}
                placeholder="e.g. 4.9 stars on Google"
              />
            )}
          />
        </Field>
      </Section>
      )}

      {/* SECTION 9: Customer Reviews */}
      {show("customer-reviews") && (
      <div>
      <Section title="Customer Reviews" subtitle="Optional" collapsible={false}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Average Rating">
            <TextInput
              type="number"
              value={form.averageRating}
              onChange={(v) => update("averageRating", v)}
              placeholder="4.9"
            />
          </Field>
          <Field label="Review Count">
            <TextInput
              type="number"
              value={form.totalReviewCount}
              onChange={(v) => update("totalReviewCount", v)}
              placeholder="1051"
            />
          </Field>
          <Field label="Review Source URL">
            <TextInput
              type="url"
              value={form.reviewSourceUrl}
              onChange={(v) => update("reviewSourceUrl", v)}
              placeholder="https://share.google/..."
            />
          </Field>
        </div>

        <Field
          label="CTA Buttons"
          hint="Up to 2 call-to-action buttons rendered above the fold on each page."
        >
          <RepeaterList<CTAButton>
            items={form.ctaButtons}
            onChange={(v) => update("ctaButtons", v)}
            max={2}
            blank={() => ({ text: "", url: "" })}
            addLabel="+ Add CTA Button"
            renderItem={(item, _i, set) => (
              <div className="grid grid-cols-2 gap-2">
                <TextInput
                  value={item.text}
                  onChange={(v) => set({ ...item, text: v })}
                  placeholder="Button text (e.g. Book Appointment)"
                />
                <TextInput
                  type="url"
                  value={item.url}
                  onChange={(v) => set({ ...item, url: v })}
                  placeholder="https:// or tel:"
                />
              </div>
            )}
          />
        </Field>

        <Field
          label="Trust Badges"
          hint="Up to 4 small badges shown near the CTAs."
        >
          <RepeaterList<TrustBadge>
            items={form.trustBadges}
            onChange={(v) => update("trustBadges", v)}
            max={4}
            blank={() => ({ text: "" })}
            addLabel="+ Add Trust Badge"
            renderItem={(item, _i, set) => (
              <TextInput
                value={item.text}
                onChange={(v) => set({ text: v })}
                placeholder="e.g. 4.9★ on Google"
              />
            )}
          />
        </Field>
      </Section>

      {/* SECTION 10: Local Details */}
      </div>
      )}
      {show("local-details") && (
      <div>
      <Section title="Local Details" collapsible={false}>
        <Field
          hint="Neighborhood notes, climate considerations, local events, community ties. Drives geo-relevance content."
        >
          <Textarea
            value={form.localDetails}
            onChange={(v) => update("localDetails", v)}
            placeholder="Neighborhood notes, climate, community ties..."
            rows={5}
          />
        </Field>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleAutoFillLocalDetails}
          disabled={fillingSection != null}
        >
          {fillingSection === "local-details"
            ? "Generating…"
            : "✨ Auto-generate from address"}
        </Button>
      </Section>

      {/* SECTION 11: Image Preferences */}
      </div>
      )}
      {show("image-preferences") && (
      <div>
      <Section title="Image Preferences" collapsible={false}>
        <Field label="Default Image Style">
          <Select
            value={form.defaultImageStyle}
            onChange={(v) => update("defaultImageStyle", v)}
            options={IMAGE_STYLE_OPTIONS}
          />
        </Field>
        <Field
          label="Brand Style Guide"
          hint="Notes for image generation: avoid, prefer, mood, color directives."
        >
          <Textarea
            value={form.brandStyleGuide}
            onChange={(v) => update("brandStyleGuide", v)}
            placeholder="Avoid stock-looking photos. Prefer warm tones. Diverse patient demographics..."
            rows={3}
          />
        </Field>
      </Section>
      </div>
      )}

      {/* SECTION 12: Prompt Customization (Advanced) — matches Core 30
          reference layout. Default writing model + default image style persist;
          image style notes and custom content prompt are form-only for now
          (not yet threaded into the generation pipeline). */}
      {show("prompt-customization") && (
      <div>
      <Section title="Prompt Customization" subtitle="Advanced" collapsible={false}>
        <Field
          label="Default writing model"
          hint="Which AI model to use for article generation. Leave on Platform default to use the platform setting."
        >
          <Select
            value={form.writingModel}
            onChange={(v) => update("writingModel", v)}
            options={PROMPT_MODEL_OPTIONS}
          />
        </Field>

        <Field
          label="Default image style"
          hint="Visual style applied to generated images for this client."
        >
          <Select
            value={form.defaultImageStyle}
            onChange={(v) => update("defaultImageStyle", v)}
            options={IMAGE_STYLE_OPTIONS}
          />
        </Field>

        <Field
          label="Image style notes"
          hint="Specific visual instructions for image generation — style, mood, color palette, subjects to avoid."
        >
          <Textarea
            value={form.imageStyleNotes}
            onChange={(v) => update("imageStyleNotes", v)}
            placeholder="Warm, natural lighting. Show real people, not stock photo models. Blue and white color palette..."
            rows={3}
          />
        </Field>

        <Field
          label="Custom content writing prompt"
          hint="Override the default article writing prompt for this client. Leave blank to use the platform prompt."
        >
          <Textarea
            value={form.customContentPrompt}
            onChange={(v) => update("customContentPrompt", v)}
            placeholder="Leave blank to use the platform default prompt..."
            rows={4}
          />
        </Field>

        <div className="my-4">
          <HowCustomPromptsWork />
        </div>

        <PromptOverrides />
      </Section>
      </div>
      )}

      {/* SECTION 13: External Links */}
      {show("external-links") && (
      <div>
      <Section title="External Links" subtitle="Optional" collapsible={false}>
        <p className="text-text font-semibold text-sm mb-1">
          Curated Authority Domains
        </p>
        <p className="text-text-subtle text-xs mb-2">
          Uncheck any domains that are competitors or irrelevant for this
          client. Free-form additions go in the chip field below.
        </p>
        <div className="flex items-center gap-3 mb-3 text-xs">
          <button
            type="button"
            className="text-accent font-medium hover:underline"
            onClick={() => update("externalLinkDomains", new Set(DEFAULT_EXTERNAL_DOMAINS))}
          >
            Select All
          </button>
          <span className="text-text-subtle">·</span>
          <button
            type="button"
            className="text-accent font-medium hover:underline"
            onClick={() => update("externalLinkDomains", new Set())}
          >
            Deselect All
          </button>
        </div>
        <CheckboxGrid
          items={DEFAULT_EXTERNAL_DOMAINS}
          selected={form.externalLinkDomains}
          onChange={(next) => update("externalLinkDomains", next)}
          columns={3}
        />

        <div className="mt-4">
          <Field
            label="Custom Authority Domains"
            hint="Add industry-specific domains not in the preset list above."
          >
            <div className="flex gap-2">
              <div className="flex-1">
                <TextInput
                  value={form.customDomainText}
                  onChange={(v) => update("customDomainText", v)}
                  placeholder="e.g. iapmo.org, ashrae.org"
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const v = form.customDomainText.trim().toLowerCase();
                  if (!v) return;
                  const next = new Set(form.externalLinkDomains);
                  next.add(v);
                  update("externalLinkDomains", next);
                  update("customDomainText", "");
                }}
              >
                Add
              </Button>
            </div>
          </Field>
        </div>

        <div className="mt-2">
          <Field
            label="Excluded Domains"
            hint="Domains to never link to — competitors, irrelevant sites."
          >
            <ChipTextField
              value={form.excludedDomainText}
              onChange={(v) => update("excludedDomainText", v)}
              placeholder="e.g. competitor.com"
            />
          </Field>
        </div>
      </Section>
      </div>
      )}

      {/* SECTION 14: YouTube / Video */}
      {show("youtube-video") && (
      <div>
      <Section title="YouTube / Video" subtitle="Optional" collapsible={false}>
        {/* Quota warning banner — matches reference */}
        <div className="flex gap-2 bg-warning/10 border border-warning/40 rounded-lg px-3 py-2.5 mb-4">
          <span className="text-warning text-sm leading-none mt-0.5">⚠</span>
          <p className="text-text-muted text-xs">
            <span className="font-semibold text-text">Important:</span> YouTube
            limits each Google Cloud project to approximately 6 video uploads
            per day on the default quota. This limit is shared across all
            clients connected to the same Google Cloud project. For higher
            volume, request a quota increase from Google — approval typically
            takes 1–2 weeks.
          </p>
        </div>

        <div className="rounded-lg border border-border bg-bg-subtle p-4 mb-4">
          <p className="text-warning text-sm font-medium mb-1">
            ⚠ Google OAuth app credentials not configured
          </p>
          <p className="text-text-subtle text-xs mb-3">
            Tokens are stored only for your account and used only to publish on
            your behalf. Enter your Google Cloud OAuth credentials — create them
            at console.cloud.google.com under "APIs &amp; Services" →
            "Credentials". Enable the YouTube Data API v3, create an OAuth 2.0
            Client ID (Web application), and add the authorized redirect URI
            shown in your deployment.
          </p>
          <div className="space-y-3">
            <Field label="Google Client ID">
              <TextInput
                value=""
                onChange={() => {}}
                placeholder="Google Client ID"
              />
            </Field>
            <Field label="Google Client Secret">
              <TextInput
                value=""
                onChange={() => {}}
                placeholder="Google Client Secret"
              />
            </Field>
            <Button variant="secondary" size="sm" onClick={() => {}}>
              Save Credentials
            </Button>
          </div>
          <p className="text-text-subtle text-xs mt-3">
            Enter these directly here — for security, credential entry isn't
            auto-filled. (Connection wiring lands in a later update.)
          </p>
        </div>

        <Field
          label="Video voiceover"
          hint="Voice used for AI-generated video narration."
        >
          <Select
            value={form.videoVoiceover}
            onChange={(v) => update("videoVoiceover", v)}
            options={VIDEO_VOICEOVER_OPTIONS}
          />
        </Field>
      </Section>
      </div>
      )}

      {/* Floating save bar — only appears when there are unsaved changes (or
          mid-save / just-saved). Solid card background, constrained to the
          content column width, floating above the content. */}
      {(isDirty || saving || saveStatus) && (
      <div className="sticky bottom-4 z-20 mt-4">
        <div className="bg-bg-card border border-border rounded-lg shadow-lg px-4 py-2.5 flex items-center justify-end gap-3">
          {saveStatus && (
            <span
              className={`text-sm mr-auto ${
                saveStatus.kind === "success" ? "text-success" : "text-danger"
              }`}
            >
              {saveStatus.message}
            </span>
          )}
          {isDirty && !saveStatus && (
            <span className="text-text-muted text-sm mr-auto">Unsaved changes</span>
          )}
          <Button variant="primary" onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? "Saving..." : isDirty ? "Save Settings" : "Saved"}
          </Button>
        </div>
      </div>
      )}
      </div>
    </div>
  );
}

// fix258: Logo URL field — a narrower input that scrolls to show the END of the
// URL (the meaningful filename, e.g. "...PrimaryLogo_Color.png") when not
// focused, plus an "Open in New Tab" button to preview whatever URL is present
// (AI-filled or hand-entered). On focus it scrolls back to the start so editing
// from the beginning still works normally.
function LogoUrlInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  // When not focused, keep the input scrolled to the far right so the filename
  // (end of the URL) is what's visible.
  useEffect(() => {
    const el = inputRef.current;
    if (!el || focused) return;
    el.scrollLeft = el.scrollWidth;
  }, [value, focused]);

  const trimmed = value.trim();
  const canOpen = /^https?:\/\//i.test(trimmed);

  function openInNewTab() {
    if (!canOpen) return;
    window.open(trimmed, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="flex items-stretch gap-2">
      <input
        ref={inputRef}
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          setFocused(true);
          // jump to start for natural left-to-right editing
          requestAnimationFrame(() => {
            if (inputRef.current) inputRef.current.scrollLeft = 0;
          });
        }}
        onBlur={() => setFocused(false)}
        placeholder="https://.../logo.png"
        className="min-w-0 flex-1 bg-bg-card border border-border rounded-md px-3 py-2 text-text placeholder:text-text-placeholder focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition"
      />
      <button
        type="button"
        onClick={openInNewTab}
        disabled={!canOpen}
        title={canOpen ? "Open this URL in a new tab" : "Enter a valid URL first"}
        className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-border bg-bg-card text-text-muted hover:text-accent hover:border-accent disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
        Open in New Tab
      </button>
    </div>
  );
}

// fix269: map a "To reach 100%" gap field to the settings section that contains
// it, so clicking a gap row jumps to the right section.
function gapSectionFor(gapKey: string): string {
  const map: Record<string, string> = {
    aboutBusiness: "about-business",
    uniqueSellingPoints: "about-business",
    credentials: "credentials",
    writingVoiceProfile: "voice-tone",
    voiceTones: "voice-tone",
    pointOfView: "voice-tone",
    wordsToAlwaysUse: "terminology",
    wordsToNeverUse: "terminology",
    writingSamples: "voice-tone",
    targetAudience: "target-audience",
    localDetails: "local-details",
    specificRequests: "specific-requests",
    gbpServicesText: "google-business-profile",
    gbpPrimaryCategory: "google-business-profile",
    gbpSecondaryCategoriesText: "google-business-profile",
    businessAddress: "business-information",
    phoneNumber: "business-information",
  };
  return map[gapKey] ?? "business-information";
}

// JSON-serialize the form, converting Set → array so JSON.stringify works
function serializableForm(form: FormState): unknown {
  return {
    ...form,
    externalLinkDomains: Array.from(form.externalLinkDomains).sort(),
  };
}

// ============================================================
// GBP Compare (fix559)
// ============================================================
// A diff between the currently-saved GBP section and a freshly-pasted list.
// Used by "Parse + Compare" to show what would change before overwriting.

export type GbpServiceMove = { service: string; from: string; to: string };

export interface GbpDiff {
  parsed: ParsedGBP; // the incoming payload, ready to apply on confirm
  primaryChanged: boolean;
  primaryFrom: string | null;
  primaryTo: string | null;
  secondaryAdded: string[];
  secondaryRemoved: string[];
  categoriesAdded: string[]; // service categories new in the paste
  categoriesRemoved: string[]; // service categories gone from the paste
  servicesAdded: { category: string; service: string }[];
  servicesRemoved: { category: string; service: string }[];
  servicesMoved: GbpServiceMove[]; // same service, different category
  secondaryKept: number; // secondary categories present in both
  servicesKept: number; // services present in both, same category
  hasAnyContent: boolean; // paste had at least one usable field
  hasAnyChange: boolean; // diff found at least one difference
}

// Case-insensitive comparison key; trims and lowercases.
function gbpKey(s: string): string {
  return s.trim().toLowerCase();
}

// Flatten a services map into a service→category lookup (case-insensitive key,
// first category wins if a service appears twice).
function serviceCategoryIndex(map: Record<string, string[]>): Map<string, string> {
  const idx = new Map<string, string>();
  for (const [cat, services] of Object.entries(map)) {
    for (const svc of services) {
      const k = gbpKey(svc);
      if (!idx.has(k)) idx.set(k, cat);
    }
  }
  return idx;
}

export function computeGbpDiff(current: ParsedGBP, incoming: ParsedGBP): GbpDiff {
  // Primary category
  const primaryFrom = current.primaryCategory?.trim() || null;
  const primaryTo = incoming.primaryCategory?.trim() || null;
  // Only counts as a change if the paste actually supplied a primary category.
  const primaryChanged =
    primaryTo != null && gbpKey(primaryTo) !== gbpKey(primaryFrom ?? "");

  // Secondary categories (set diff, case-insensitive; only if paste supplied any)
  const curSecKeys = new Map(current.secondaryCategories.map((s) => [gbpKey(s), s]));
  const inSecKeys = new Map(incoming.secondaryCategories.map((s) => [gbpKey(s), s]));
  const pasteHasSecondary = incoming.secondaryCategories.length > 0;
  const secondaryAdded = pasteHasSecondary
    ? [...inSecKeys].filter(([k]) => !curSecKeys.has(k)).map(([, v]) => v)
    : [];
  const secondaryRemoved = pasteHasSecondary
    ? [...curSecKeys].filter(([k]) => !inSecKeys.has(k)).map(([, v]) => v)
    : [];
  const secondaryKept = pasteHasSecondary
    ? [...inSecKeys].filter(([k]) => curSecKeys.has(k)).length
    : 0;

  // Services — only diff if the paste supplied services at all. Otherwise the
  // paste is categories-only and we must NOT report every existing service as
  // "removed".
  const pasteHasServices = Object.keys(incoming.services).length > 0;

  const categoriesAdded: string[] = [];
  const categoriesRemoved: string[] = [];
  const servicesAdded: { category: string; service: string }[] = [];
  const servicesRemoved: { category: string; service: string }[] = [];
  const servicesMoved: GbpServiceMove[] = [];
  let servicesKept = 0;

  if (pasteHasServices) {
    const curCatKeys = new Map(Object.keys(current.services).map((c) => [gbpKey(c), c]));
    const inCatKeys = new Map(Object.keys(incoming.services).map((c) => [gbpKey(c), c]));
    for (const [k, v] of inCatKeys) if (!curCatKeys.has(k)) categoriesAdded.push(v);
    for (const [k, v] of curCatKeys) if (!inCatKeys.has(k)) categoriesRemoved.push(v);

    const curIdx = serviceCategoryIndex(current.services);
    const inIdx = serviceCategoryIndex(incoming.services);

    // Added or moved: walk incoming services.
    for (const [svcKey, inCat] of inIdx) {
      const curCat = curIdx.get(svcKey);
      const inDisplay = findDisplayService(incoming.services, svcKey);
      if (curCat == null) {
        servicesAdded.push({ category: inCat, service: inDisplay });
      } else if (gbpKey(curCat) !== gbpKey(inCat)) {
        servicesMoved.push({ service: inDisplay, from: curCat, to: inCat });
      } else {
        servicesKept += 1; // present in both, same category
      }
    }
    // Removed: services in current not present in incoming at all.
    for (const [svcKey] of curIdx) {
      if (!inIdx.has(svcKey)) {
        const curDisplay = findDisplayService(current.services, svcKey);
        servicesRemoved.push({ category: curIdx.get(svcKey)!, service: curDisplay });
      }
    }
  }

  const hasAnyContent =
    primaryTo != null || pasteHasSecondary || pasteHasServices;
  const hasAnyChange =
    primaryChanged ||
    secondaryAdded.length > 0 ||
    secondaryRemoved.length > 0 ||
    categoriesAdded.length > 0 ||
    categoriesRemoved.length > 0 ||
    servicesAdded.length > 0 ||
    servicesRemoved.length > 0 ||
    servicesMoved.length > 0;

  return {
    parsed: incoming,
    primaryChanged,
    primaryFrom,
    primaryTo,
    secondaryAdded,
    secondaryRemoved,
    categoriesAdded,
    categoriesRemoved,
    servicesAdded,
    servicesRemoved,
    servicesMoved,
    secondaryKept,
    servicesKept,
    hasAnyContent,
    hasAnyChange,
  };
}

// Recover the original-cased service name for a normalized key.
function findDisplayService(map: Record<string, string[]>, svcKey: string): string {
  for (const services of Object.values(map)) {
    for (const svc of services) if (gbpKey(svc) === svcKey) return svc;
  }
  return svcKey;
}

// Read-only diff panel for "Parse + Compare". Shows adds (green), removes
// (red), moves (amber), and primary/secondary changes. "Apply changes" runs
// the same overwrite as Parse + Add; "Dismiss" discards the diff with no write.
function GbpDiffPanel({
  diff,
  onApply,
  onDismiss,
}: {
  diff: GbpDiff;
  onApply: () => void;
  onDismiss: () => void;
}) {
  // A labelled group of diff lines. `tone` sets the accent color of the
  // symbol/label; the item text stays default so it's easy to read.
  function Group({
    label,
    hint,
    tone,
    symbol,
    children,
    count,
  }: {
    label: string;
    hint: string;
    tone: "success" | "danger" | "warning";
    symbol: string;
    children: ReactNode;
    count: number;
  }) {
    if (count === 0) return null;
    const toneText =
      tone === "success" ? "text-success" : tone === "danger" ? "text-danger" : "text-warning";
    return (
      <div>
        <div className={`text-xs font-semibold ${toneText} flex items-center gap-1.5`}>
          <span className="inline-flex w-4 justify-center">{symbol}</span>
          {label} <span className="text-text-subtle font-normal">({count})</span>
        </div>
        <p className="text-text-subtle text-[11px] ml-5 mb-1">{hint}</p>
        <ul className="ml-5 space-y-0.5 text-xs text-text">{children}</ul>
      </div>
    );
  }

  const addedTotal = diff.servicesAdded.length + diff.secondaryAdded.length;
  const removedTotal = diff.servicesRemoved.length + diff.secondaryRemoved.length;

  return (
    <div className="mt-3 border border-border rounded-lg bg-bg-subtle p-3.5 space-y-3">
      {/* Heading + one-line explanation of what this panel is */}
      <div>
        <div className="text-text font-semibold text-sm">
          Compare — your pasted list vs. what's saved now
        </div>
        <p className="text-text-muted text-xs mt-0.5">
          Nothing has changed yet. This shows what would happen if you apply the
          pasted list. <span className="text-text">Added</span> = in your paste
          but not saved yet. <span className="text-text">Removed</span> = saved
          now but missing from your paste.
        </p>
      </div>

      {diff.hasAnyChange ? (
        <>
          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] border-y border-border-subtle py-1.5">
            <span className="text-success font-medium">＋ Added</span>
            <span className="text-danger font-medium">－ Removed</span>
            <span className="text-warning font-medium">↔ Moved / changed</span>
            <span className="text-text-subtle">
              greyed = current value being replaced
            </span>
          </div>

          <div className="space-y-3">
            {/* Primary category change */}
            {diff.primaryChanged && (
              <div>
                <div className="text-xs font-semibold text-warning flex items-center gap-1.5">
                  <span className="inline-flex w-4 justify-center">↔</span>
                  Primary category changes
                </div>
                <div className="ml-5 text-xs text-text flex items-center gap-2 mt-0.5">
                  <span className="px-1.5 py-0.5 rounded bg-bg-card border border-border text-text-muted line-through">
                    {diff.primaryFrom ?? "(none)"}
                  </span>
                  <span className="text-text-subtle">→</span>
                  <span className="px-1.5 py-0.5 rounded bg-bg-card border border-border font-medium">
                    {diff.primaryTo}
                  </span>
                </div>
              </div>
            )}

            {/* Moves — clean "was → now", no highlighted substrings */}
            {diff.servicesMoved.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-warning flex items-center gap-1.5">
                  <span className="inline-flex w-4 justify-center">↔</span>
                  Moving to a different category{" "}
                  <span className="text-text-subtle font-normal">
                    ({diff.servicesMoved.length})
                  </span>
                </div>
                <p className="text-text-subtle text-[11px] ml-5 mb-1">
                  Same service, filed under a different category in your paste.
                </p>
                <ul className="ml-5 space-y-0.5 text-xs text-text">
                  {diff.servicesMoved.map((m) => (
                    <li key={`mv-${m.service}`} className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{m.service}</span>
                      <span className="text-text-subtle">·</span>
                      <span className="text-text-muted line-through">{m.from}</span>
                      <span className="text-text-subtle">→</span>
                      <span className="text-text">{m.to}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Added */}
            <Group
              label="Being added"
              hint="These appear in your pasted list but aren't saved yet."
              tone="success"
              symbol="＋"
              count={addedTotal}
            >
              {diff.secondaryAdded.map((s) => (
                <li key={`sec-add-${s}`}>
                  {s} <span className="text-text-subtle">— secondary category</span>
                </li>
              ))}
              {diff.servicesAdded.map((s) => (
                <li key={`svc-add-${s.category}-${s.service}`}>
                  {s.service}{" "}
                  <span className="text-text-subtle">in {s.category}</span>
                </li>
              ))}
            </Group>

            {/* Removed */}
            <Group
              label="Being removed"
              hint="These are saved now but missing from your pasted list, so applying would drop them."
              tone="danger"
              symbol="－"
              count={removedTotal}
            >
              {diff.secondaryRemoved.map((s) => (
                <li key={`sec-rem-${s}`}>
                  {s} <span className="text-text-subtle">— secondary category</span>
                </li>
              ))}
              {diff.servicesRemoved.map((s) => (
                <li key={`svc-rem-${s.category}-${s.service}`}>
                  {s.service}{" "}
                  <span className="text-text-subtle">was in {s.category}</span>
                </li>
              ))}
            </Group>
          </div>

          {/* Scope summary — what stays the same */}
          <p className="text-text-subtle text-[11px] border-t border-border-subtle pt-2">
            Unchanged: {diff.servicesKept} service
            {diff.servicesKept === 1 ? "" : "s"}
            {diff.secondaryKept > 0 &&
              `, ${diff.secondaryKept} secondary categor${diff.secondaryKept === 1 ? "y" : "ies"}`}
            {" "}stay exactly as they are.
          </p>
        </>
      ) : (
        <p className="text-success text-xs">
          ✓ Your pasted list matches what's already saved. Nothing would change.
        </p>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button
          variant="primary"
          size="sm"
          onClick={onApply}
          disabled={!diff.hasAnyChange}
        >
          Apply changes
        </Button>
        <button
          type="button"
          className="text-xs text-text-muted hover:underline"
          onClick={onDismiss}
        >
          Dismiss
        </button>
        <span className="text-text-subtle text-xs ml-1">
          Applying overwrites Primary, Secondary, and Services below.
        </span>
      </div>
    </div>
  );
}

/**
 * Parse a US-style freeform address into { city, state, zip }.
 *
 * Accepts common shapes:
 *   "123 Main St, Houston, TX 77043"
 *   "Houston, TX 77043"
 *   "Houston, TX"
 *   "77043"
 *   "Houston Texas 77043"  (no commas, US state name spelled out)
 *
 * Returns empty strings for parts it can't confidently extract. Callers
 * should validate (city+state OR zip) before relying on the result.
 */
/**
 * fix256: reflow a single-line address into two lines, breaking BEFORE the city
 * so the street is on line 1 and "City, ST ZIP" is on line 2. Self-contained
 * (doesn't depend on the zip-sensitive parseAddressLocality). If the structure
 * isn't recognized (or it's already multi-line), the input is returned
 * unchanged.
 *
 * Strategy: US addresses are conventionally "street, city, ST ZIP". We split on
 * commas; the locality (city onward) is the LAST TWO comma segments — i.e. the
 * "City" segment plus the "ST ZIP" segment. Everything before that is the
 * street. If there's only one comma, we break at that comma.
 */
function formatAddressTwoLines(address: string): string {
  const raw = (address ?? "").trim();
  if (!raw) return raw;
  if (/\n/.test(raw)) return raw; // already multi-line — leave as-is.

  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return raw; // can't confidently find the city.

  let streetParts: string[];
  let localityParts: string[];
  if (parts.length >= 3) {
    // street..., City, ST ZIP  →  line2 = last two segments (City + "ST ZIP").
    streetParts = parts.slice(0, parts.length - 2);
    localityParts = parts.slice(parts.length - 2);
  } else {
    // Exactly two segments: "street, City ST ZIP" → break at the comma.
    streetParts = [parts[0]!];
    localityParts = [parts[1]!];
  }

  const street = streetParts.join(", ").trim();
  const locality = localityParts.join(", ").trim();
  if (!street || !locality) return raw;
  return `${street}\n${locality}`;
}

function parseAddressLocality(address: string): {
  city: string;
  state: string;
  zip: string;
} {
  const trimmed = (address ?? "").trim();
  if (!trimmed) return { city: "", state: "", zip: "" };

  // Pull out a 5-digit (or 5+4) ZIP if present.
  let zip = "";
  const zipMatch = trimmed.match(/\b(\d{5})(?:-\d{4})?\b/);
  if (zipMatch) zip = zipMatch[1] ?? "";

  // Strip the ZIP for downstream parsing.
  const withoutZip = zip ? trimmed.replace(zipMatch![0], "").trim() : trimmed;

  // Try comma-separated form: ".., City, ST" — last two comma parts.
  let city = "";
  let state = "";
  if (withoutZip.includes(",")) {
    const parts = withoutZip
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    // Last part should be the state (2-letter code or full name).
    // Second-to-last is city.
    if (parts.length >= 2) {
      const last = parts[parts.length - 1]!.trim();
      const second = parts[parts.length - 2]!.trim();
      if (/^[A-Za-z]{2}$/.test(last) || isUsStateName(last)) {
        state = normalizeStateAbbrev(last);
        city = second;
      } else {
        // Last part isn't a state — assume it's city.
        city = last;
      }
    } else if (parts.length === 1) {
      city = parts[0]!;
    }
  } else {
    // No commas. Try "<city words> <ST>" or "<city> <state name>" pattern.
    const tokens = withoutZip.split(/\s+/).filter(Boolean);
    if (tokens.length >= 2) {
      const last = tokens[tokens.length - 1]!;
      if (/^[A-Za-z]{2}$/.test(last)) {
        state = last.toUpperCase();
        city = tokens.slice(0, -1).join(" ");
      } else if (isUsStateName(last)) {
        state = normalizeStateAbbrev(last);
        city = tokens.slice(0, -1).join(" ");
      } else {
        city = withoutZip;
      }
    } else if (tokens.length === 1 && !zip) {
      city = tokens[0]!;
    }
  }

  return { city: city.trim(), state: state.trim(), zip };
}

const US_STATE_TO_ABBREV: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR",
  california: "CA", colorado: "CO", connecticut: "CT", delaware: "DE",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID",
  illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS",
  kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS",
  missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV",
  "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM",
  "new york": "NY", "north carolina": "NC", "north dakota": "ND",
  ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA",
  "rhode island": "RI", "south carolina": "SC", "south dakota": "SD",
  tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY",
};

function isUsStateName(s: string): boolean {
  return US_STATE_TO_ABBREV[s.toLowerCase()] !== undefined;
}

function normalizeStateAbbrev(s: string): string {
  if (/^[A-Za-z]{2}$/.test(s)) return s.toUpperCase();
  return US_STATE_TO_ABBREV[s.toLowerCase()] ?? s;
}
